


























import Vue from 'vue'

import { EventService } from 'base/services/EventService'

import { WidgetType } from '..'
import { DragDropAction, DragDropService, DragDropTarget } from '../drag-drop'
import { PermutatorService } from '../permutator'
import Widget from '../widget/BaseWidget.vue'
import WidgetRowControls from './WidgetRowControls.vue'
import { WidgetRow } from './WidgetRowInterface'

export default Vue.extend({
  components: {
    WidgetRowControls,
    Widget
  },
  props: {
    isEditable: {
      type: Boolean,
      default: false
    },
    row: {
      type: Object as () => WidgetRow,
      required: true
    },
    numColsAvailable: {
      type: Number,
      default: 32
    },
    numRowsInContainer: {
      type: Number,
      required: true
    },
    dashboardSettings: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      widgetMap: [],
      fixedRowHeight: 'auto',
      hoveredContainerUUID: '',
      dragTarget: undefined
    }
  },
  computed: {
    // Daktiviert solange kein finales Bedienkonzept existiert
    showControls() {
      return false
    },
    canMoveUp() {
      return this.row.sortindex! > 0
    },
    canMoveDown() {
      return this.row.sortindex! < this.numRowsInContainer - 1
    },
    isEmpty() {
      return this.row.widgets.length < 1
    },
    getRemainingColCount() {
      let remainingCols = this.numColsAvailable
      this.row.widgets.forEach(widget => remainingCols -= widget.size)

      return remainingCols
    },
    orderedWidgets() {
      if (!this.row.widgets) {
        return
      }

      let orderedWidgets = JSON.parse(JSON.stringify(this.row.widgets))

      return orderedWidgets.sort((a, b) => a.sortindex - b.sortindex)
    }
  },
  methods: {
    initWidgetMap() {
      let items = Array.prototype.filter.call(this.$el.children, item => item.className.split(' ').indexOf('widget') > -1)

      this.widgetMap = items.map((item: any) => {
        let rect = item.getBoundingClientRect()
        rect.item = item

        return rect
      })
    },
    rowContains(widgetUUID: string): boolean {
      return PermutatorService.rowContains(
        PermutatorService.getByUUID(this.row.wbUUID) as WidgetRow,
        widgetUUID)
    },
    getDropTargetIndex(action: DragDropAction): number {
      const widgetMap = this.widgetMap

      let numMapItems = widgetMap.length
      let placeholderIndex = -1
      let i = 0
      let widget

      // find index of placeholder
      for (;i < numMapItems;i++) {
        widget = widgetMap[i]

        if (widget.item.getAttribute('data-widget-type') === WidgetType.dragDropIndicator) {
          placeholderIndex = i
          break
        }
      }

      // when we are dragging over the row and there is no placeholder inside the row,
      // it's one of two scenarios. Either we just started dragging an item that already was part of the row and it
      // wasn't replaced with a placeholer yet or we are entering with a fresh item from outside the row.
      // In the first scenario we want to return the item's sortindex to let the PermutatorService know at which
      // position to inject the placeholder. The second scenario is just a regular drop action and we let the function
      // proceed
      if (
        (placeholderIndex === -1) &&
        this.rowContains(action.getWidget().wbUUID)
      ) {
        return action.getWidget().sortindex!
      }

      const x = action.getAbsolutePosition().x

      let targetIndex = -1
      let currentIndex = -1
      let hoveredWidget = null

      // find the placeholder's target position
      for (i = 0;i < numMapItems;i++) {
        widget = widgetMap[i]

        currentIndex++

        // check if we are dragging within the widget's bounds
        if ((x < widget.x) || (x > widget.right)) {
          continue
        }

        // don't process placeholder
        if (currentIndex === placeholderIndex) {
          targetIndex = placeholderIndex
          break
        }

        // evaluate if we are hovering over a widget-container
        const widgetUUID = widget.item.getAttribute('data-wbuuid')
        if (widgetUUID) {
          hoveredWidget = PermutatorService.getByUUID(widgetUUID)
          if (hoveredWidget && hoveredWidget.rows) {
            if (this.hoveredContainerUUID && (widgetUUID !== this.hoveredContainerUUID)) {
              EventService.$emit('drag-drop-container-leave', this.hoveredContainerUUID)
            }
            this.hoveredContainerUUID = widgetUUID
            EventService.$emit('drag-drop-container-enter', widgetUUID)
            action.showHint('Warten, um Container auszuwählen')
          }
        }

        // left or right from the item's center?
        let relPos = (x <= (widget.left + (widget.width / 2))) ? -1 : 1

        // placeholder is NOT placed inside the row yet so we decide it's injection position relative to the center
        // of the item we are currently dragging over
        if (placeholderIndex < 0) {
          targetIndex = currentIndex + 1
          if (relPos < 0) {
            targetIndex = currentIndex
          }
          break
        }

        targetIndex = currentIndex

        if (
          ((placeholderIndex < currentIndex) && (relPos < 0)) ||
          ((placeholderIndex > currentIndex) && (relPos > 0))
        ) {
          targetIndex = placeholderIndex
        }

        break
      }

      if (!hoveredWidget && this.hoveredContainerUUID) {
        EventService.$emit('drag-drop-container-leave', this.hoveredContainerUUID)
        this.hoveredContainerUUID = ''
        action.hideHint()
      }

      if (targetIndex < 0) {
        targetIndex = (placeholderIndex < 0) ? numMapItems : numMapItems - 1
      }

      return (targetIndex === placeholderIndex) ? -1 : targetIndex
    },
    onDragEnter(action: DragDropAction) {
      this.hoveredContainerUUID = ''
      this.onDragOver(action)
    },
    onDragLeave(action: DragDropAction) {
      if (this.hoveredContainerUUID) {
        EventService.$emit('drag-drop-container-leave', this.hoveredContainerUUID)
        this.hoveredContainerUUID = ''
        action.hideHint()
      }

      EventService.$emit('row-drag-item-leave', this.row, action.getWidget().isTemplate)
    },
    onDragDropIndicatorRemoved(rowUUID: string) {
      if (rowUUID !== this.row.wbUUID) {
        return
      }

      Vue.nextTick(() => this.initWidgetMap())
    },
    onDragOver(action: DragDropAction) {
      const targetIndex = this.getDropTargetIndex(action)

      // targetIndex = -1 means position didn't change
      if (targetIndex === -1) {
        return
      }

      EventService.$emit('row-drag-item-indicate', this.row, action.getWidget(), targetIndex)
      Vue.nextTick(() => this.initWidgetMap())
    },
    onDrop(action: DragDropAction) {
      EventService.$emit('row-drag-item-drop', this.row, action.getWidget())
    },
    prepareDrag(action: DragDropAction) {
      this.fixedRowHeight = this.$el.getBoundingClientRect().height + 'px'
      this.initWidgetMap()
    },
    finishDrag(action: DragDropAction) {
      this.fixedRowHeight = 'auto'
    },
  },
  created() {
    // this.rebuildSortindex()
  },
  mounted() {
    this.dragTarget = new DragDropTarget(this.$el)
      .onRequestContainer(() => PermutatorService.getRowContainer(this.row.wbUUID)!)
      .allow(this.$el.getAttribute('data-drag-accept'))
      .onDragEnter(this.onDragEnter.bind(this))
      .onDragLeave(this.onDragLeave.bind(this))
      .onDragOver(this.onDragOver.bind(this))
      .onDrop(this.onDrop.bind(this))

    DragDropService.registerDragTarget(this.dragTarget)

    EventService.$on('drag-drop-service-reinitialise', this.initWidgetMap)
    EventService.$on('drag-drop-service-start-drag', this.prepareDrag)
    EventService.$on('drag-drop-service-end-drag', this.finishDrag)
    EventService.$on('drag-drop-indicator-removed', this.onDragDropIndicatorRemoved)
  },
  destroyed() {
    if (this.dragTarget) {
      DragDropService.unregisterDragTarget(this.dragTarget)
    }

    EventService.$off('drag-drop-service-reinitialise', this.initWidgetMap)
    EventService.$off('drag-drop-service-start-drag', this.prepareDrag)
    EventService.$off('drag-drop-service-end-drag', this.finishDrag)
    EventService.$off('drag-drop-indicator-removed', this.onDragDropIndicatorRemoved)
  }
})
